/**
 * @fileoverview Welcome page component with client-specific branding
 *
 * This file implements the welcome page which serves as the entry point for users
 * after authentication. It displays client-specific branding and content, and handles
 * the onboarding flow by determining which registration steps the user needs to complete.
 *
 * Key features:
 * - Dynamic client branding (logos, images, text)
 * - Onboarding flow management
 * - Profile completion verification
 * - Conditional redirection based on profile status
 */

import React, { useContext, useEffect, useState } from 'react';
import { Button, ButtonVariant, IconName, ButtonIconAlign } from '@eightfold.ai/octuple';
import Header from '../commons/Header';
import { actionButtonStyle } from '../commons/commonStyles';
import Footer from '../commons/Footer';
import Api from '../Api';
import { globalContext } from '../store';
import { useNavigate } from 'react-router';
import { checkUnauthorized, checkAuthErr, getBrandValues, getBrandImages, dispatchToGlobalState } from '../commons/commonFunctions';
import { clientInfo } from '../commons/ClientDynamicInfo';
/**
 * Welcome page component that handles user entry and onboarding flow
 *
 * This component loads client-specific branding and content, checks the user's
 * profile completion status, and redirects to the appropriate onboarding step
 * or dashboard based on which parts of the profile are complete.
 *
 * @returns {JSX.Element} The rendered welcome page with client-specific branding
 */
const WelcomePage = () => {
  const navigate = useNavigate();
  const { globalState, dispatch } = useContext(globalContext);
  // State for welcome page content and branding
  const [welcomeText, setWelcomeText] = useState<any>('');
  const [welcomeTitleText, setWelcomeTitleText] = useState<any>('');
  const [leftPanelImg, setLeftPanelImg] = useState<string>('');
  const [headerLogo, setHeaderLogo] = useState<string>('');
  const [primaryColor, setPrimaryColor] = useState<string>('#BA3A19');
  const [showLoader, setShowLoader] = useState(false);
  const [privacyPolicyLastUpdated, setPrivacyPolicyLastUpdated] = useState<any>(null);
  const title = globalState?.clientData?.name;
  let fetchLookupData = false;
  const countryArrEth = ['United States of America (the)', 'Canada', 'Australia', 'United Kingdom of Great Britain and Northern Ireland (the)'];
  const countryArrLGBTQ = ['United States of America (the)', 'United Kingdom of Great Britain and Northern Ireland (the)'];
  const countryArrVeteranStatus = ['United States of America (the)'];
  /**
   * Effect to load client-specific branding and content
   *
   * This loads branding elements (welcome text, title, images, logos) based on:
   * 1. Client-specific configuration if available in clientInfo
   * 2. Branding values from the client data in global state
   * 3. Default values as a fallback
   */

  const applyGlobalBranding = () => {
    const info = clientInfo[title];
    setWelcomeText(info.welcomeText.split('\n'));
    setWelcomeTitleText(info.welcomeTextTitle);
    setLeftPanelImg(info.leftImage);
    setHeaderLogo(info.headerLogo);
  };

  const isNonEmpty = (v: any) => v !== null && v !== undefined && v !== '';

  const applyClientBranding = () => {
    const welcomeBodyText = getBrandValues(globalState?.clientData?.branding, 'WELCOME_PAGE_BODY_TEXT');
    const welcomeTitleText = getBrandValues(globalState?.clientData?.branding, 'WELCOME_PAGE_TITLE');
    const leftPanelImgId = getBrandValues(globalState?.clientData?.branding, 'WELCOME_PAGE_IMAGE');
    const headerLogoId = getBrandValues(globalState?.clientData?.branding, 'COMPANY_LOGO');

    if (isNonEmpty(welcomeBodyText?.value)) {
      setWelcomeText(welcomeBodyText.value.split('\n'));
    } else {
      setWelcomeText(clientInfo['*'].welcomeText.split('\n'));
    }

    if (isNonEmpty(welcomeTitleText?.value)) {
      setWelcomeTitleText(welcomeTitleText.value);
    } else {
      setWelcomeTitleText(clientInfo['*'].welcomeTextTitle);
    }

    if (globalState?.leftPanelImg) {
      setLeftPanelImg(globalState.leftPanelImg);
    } else {
      getBrandImages(leftPanelImgId, setLeftPanelImg, 'leftImage', dispatch, 'SET_LEFT_PANEL_IMG');
    }

    if (globalState?.headerLogo) {
      setHeaderLogo(globalState.headerLogo);
    } else {
      getBrandImages(headerLogoId, setHeaderLogo, 'headerLogo', dispatch, 'SET_HEADER_LOGO');
    }

    if (globalState?.primaryColor) {
      setPrimaryColor(globalState.primaryColor);
    } else {
      const primColor = getBrandValues(globalState?.clientData?.branding, 'PRIMARY_COLOR')?.value || '#BA3A19';
      dispatchToGlobalState(setPrimaryColor, primColor, dispatch, 'SET_PRIMARY_COLOR');
    }
  };

  useEffect(() => {
    if (title && clientInfo.hasOwnProperty(title)) {
      applyGlobalBranding();
    } else {
      applyClientBranding();
    }

    //Get privacy policy Attachment details
    Api.getAttachmentDetails(process.env.REACT_APP_PRIVACY_POLICY_ATTACHMENTID)
      .then((response: any) => {
        if (response?.success) {
          setPrivacyPolicyLastUpdated(response?.response?.data?.data?.[0]?.lastUpdatedTs);
        }
      })
      .catch((error: any) => {
        checkAuthErr(error, dispatch);
      });
  }, []);

  let loginToken: any = JSON.parse(sessionStorage.getItem('loginToken') ?? 'false');

  /**
   * Client ID from session storage
   * Identifies which client/organization the user belongs to
   */
  let clientId: any = JSON.parse(sessionStorage.getItem('clientId') ?? 'false');

  /**
   * API request payload for fetching leader profile
   */
  const payload = {
    email: loginToken?.idToken?.claims?.email,
    clientId: clientId,
  };

  /**
   * Utility function to check if all specified fields exist in an object
   *
   * Used to determine if sections of the user profile are complete by
   * verifying that all required fields for that section have values.
   *
   * @param {any} obj - The object to check (user profile data)
   * @param {string[]} fields - Array of field paths to verify
   * @returns {boolean} True if all fields exist in the object
   */

  const listPrepared = (item: any) => {
    const listWithSelect = item?.dsListEntries?.map((i: any) => {
      return {
        text: i.dsListEntry,
        value: i.dsListEntry,
        subList: subListPrepared(i),
      };
    });
    listWithSelect.unshift({ text: 'Select', value: '' });
    return listWithSelect;
  };

  const subListPrepared = (i: any) => {
    if (i?.dsSubList && i?.dsSubList.length > 0) {
      const subList = i?.dsSubList?.map((list: any) => {
        return {
          text: list.dsSubListEntry,
          value: list.dsSubListEntry,
        };
      });
      subList.unshift({ text: 'Select', value: '' });
      return subList;
    }
  };

  const getLookUpData = () => {
    const payload = {
      clientId: clientId,
    };
    if (fetchLookupData === false) {
      fetchLookupData = true;
      Api.getLookUp(payload)
        .then((response: any) => {
          if (response.success) {
            const responseData = response?.response?.data?.data?.[0]?.demographicSurveyLookups;
            const newData = responseData?.map((item: any) => {
              return {
                dropDownName: item.dsListName,
                dropDownValues: listPrepared(item),
              };
            });
            dispatch({
              type: 'SET_LOOKUP_DATA',
              payload: newData,
            });
            setShowLoader(false);
          } else {
            setShowLoader(false);
            checkUnauthorized(response, dispatch);
          }
        })
        .catch(err => {
          setShowLoader(false);
          checkAuthErr(err, dispatch);
        });
    }
  };

  const isFieldEmpty = (value: any): boolean => {
    return value === null || value === undefined || value === '' || value === 'null';
  };

  const countDemographicFields = (clientDemographicConfig: any, leaderDemographicData: any) => {
    if (!clientDemographicConfig) {
      return { requiredCount: 0, nonFilledCount: 0, filledCount: 0, nonFilledFields: [] };
    }

    let requiredCount = 0;
    let nonFilledCount = 0;
    const nonFilledFields: string[] = [];

    // Iterate through client config to find required fields (marked as true)
    Object.keys(clientDemographicConfig).forEach(fieldKey => {
      if (clientDemographicConfig[fieldKey] === true) {
        requiredCount++;

        fieldKey = fieldKey === 'disabilityStatus' ? 'veteranStatus' : fieldKey;
        // Check if the field exists and is filled in leader data
        const fieldValue = leaderDemographicData?.[fieldKey];
        if (isFieldEmpty(fieldValue)) {
          nonFilledCount++;
          nonFilledFields.push(fieldKey);
        }
      }
    });

    const filledCount = requiredCount - nonFilledCount;

    return { requiredCount, nonFilledCount, filledCount, nonFilledFields };
  };

  /**
   * Determines which page to redirect to based on profile completion status
   *
   * This function:
   * 1. Adds country-specific fields to required fields array
   * 2. Checks which sections of the profile are incomplete
   * 3. Redirects to the appropriate onboarding step or dashboard
   *
   * @param {any} res - User profile data from API response
   */
  const redirectionPaths = (res: any) => {
    // Add country-specific fields for US and UK users
    const acceptedTs = res?.applications?.leaderPortal?.privacyPolicyAcceptedTs;
    const lastUpdated: any = privacyPolicyLastUpdated;
    const acceptedDate = new Date(acceptedTs);
    const lastUpdatedDate = new Date(lastUpdated);
    // Count required and non-filled demographic fields

    const fieldCounts = countDemographicFields(globalState?.clientData?.demographicSurveyData, res?.demographicSurveyData);
    // Filter country-specific fields based on country of residence
    const countryOfResidence = res?.demographicSurveyData?.countryOfResidence;
    const filteredNonFilledFields = fieldCounts.nonFilledFields.filter((field: string) => {
      // Exclude terminationDate from non-filled fields
      if (!fieldCounts.nonFilledFields.includes('hireDate') && field === 'terminationDate') {
        return false;
      }

      // If countryOfResidence is not defined, include all fields (including country-specific ones)
      if (!countryOfResidence || countryOfResidence === '') {
        return true;
      }

      // If field is ethnicity, only include if country is in countryArrEth
      if (field === 'ethnicity' || field === 'gender' || field === 'age') {
        return countryArrEth.includes(countryOfResidence);
      }
      // If field is lgbtq, only include if country is in countryArrLGBTQ
      if (field === 'lgbtq') {
        return countryArrLGBTQ.includes(countryOfResidence);
      }
      // If field is veteranStatus, only include if country is in countryArrVeteranStatus
      if (field === 'disabilityStatus' || field === 'veteranStatus') {
        return countryArrVeteranStatus.includes(countryOfResidence);
      }
      // For all other fields, include them
      return true;
    });

    // Recalculate counts based on filtered fields
    const filteredFieldCounts = {
      requiredCount: fieldCounts.requiredCount,
      nonFilledFields: filteredNonFilledFields,
      nonFilledCount: filteredNonFilledFields.length,
      filledCount: fieldCounts.requiredCount - filteredNonFilledFields.length,
    };

    // if (res?.isProfileCompleted && ((lastUpdatedDate > acceptedDate) ) {
    if (res?.isProfileCompleted && ((lastUpdatedDate > acceptedDate) || (filteredFieldCounts.requiredCount > filteredFieldCounts.filledCount))) {
      getLookUpData();

      navigate('/welcome-back', {
        state: {
          leaderProfileId: res?.id,
          nonFilledFields: filteredFieldCounts.nonFilledFields,
          acceptedDate: acceptedDate,
          lastUpdatedDate: lastUpdatedDate,
          demographicFieldsInfo: {
            requiredCount: filteredFieldCounts.requiredCount,
            filledCount: filteredFieldCounts.filledCount,
            nonFilledCount: filteredFieldCounts.nonFilledCount,
          },
        },
      });
    } else {
      if (!res?.isProfileCompleted) {
        navigate('/register-account');
      } else if (res?.isProfileCompleted) {
         navigate('/dashboard');
      }

    }
  };

  /**
   * Handler for the Continue button click
   *
   * Fetches the user's profile data from the API and redirects
   * to the appropriate page based on profile completion status.
   * Shows a loading indicator during the API call.
   */
  const onClickContinue = () => {
    setShowLoader(true);
    Api.getLeaderProfile(payload)
      .then((response: any) => {
        if (response?.success) {
          const res = response?.response?.data?.data?.[0];
          // Store leader data in global state
          dispatch({
            type: 'SET_LEADER_DATA',
            payload: res,
          });

          //Check if Okta status exists in the response if yes then change the group
          if (res?.oktaStatus !== null && res?.oktaStatus !== undefined && "FIRST_TIME_LOGIN" === res?.oktaStatus) {
            const payload = {
              "userName": res?.email,
              "profileType": res?.profileType,
            };

            Api.updateOktaStatus(payload).then((response: any) => {
              redirectionPaths(res);
            }).catch(err => {
              redirectionPaths(res);
            });
          } else {
            // Navigate based on profile completion status
            redirectionPaths(res);
          }
        } else {
          setShowLoader(false);
          checkUnauthorized(response, dispatch);
        }
      })
      .catch(err => {
        setShowLoader(false);
        checkAuthErr(err, dispatch);
      });
  };

  return (
    <div className='mainWrapperOnboarding' style={{ backgroundImage: `url(${leftPanelImg})` }}>
      <div className='middleContentWrapper' style={{ borderLeftColor: primaryColor }}>
        {/* Header with client logo */}
        <Header title={title} headerLogo={headerLogo} />
        <hr className='headerSeparator' />

        {/* Welcome content section */}
        <div style={{ alignItems: 'unset' }}>
          <div className='contentWrapper' style={{ alignItems: 'unset' }}>
            <div>
              {/* Welcome message title */}
              <h2>{welcomeTitleText}</h2>
              <div className='middleContent'>
                {/* Welcome message paragraphs with HTML support */}
                {welcomeText && welcomeText.map((text: any) => <p className='welcomeContent' key={text} dangerouslySetInnerHTML={{ __html: text }}></p>)}
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              {/* Continue button with loading state */}
              <Button
                text='Continue'
                variant={ButtonVariant.Secondary}
                classNames='actionButton'
                style={actionButtonStyle}
                onClick={() => {
                  onClickContinue();
                }}
                iconProps={showLoader ? { path: IconName.mdiLoading, spin: true } : { path: IconName.mdiArrowRight }}
                alignIcon={ButtonIconAlign.Right}
                disabled={showLoader}
              />
            </div>
          </div>
        </div>

        {/* Page footer */}
        <Footer />
      </div>
    </div>
  );
};

/**
 * Export the WelcomePage component as the default export
 * This component is used in AppRouter.tsx for the welcome route
 */
export default WelcomePage;
